/**
 * Breadcrumb trail — the SINGLE source of truth for both the visible breadcrumb
 * UI (src/components/Breadcrumbs.tsx) and the BreadcrumbList JSON-LD
 * (src/lib/jsonld.ts). Deriving both from here guarantees the on-page trail and
 * the structured data can never drift apart.
 *
 * A page's slug maps to one crumb per path segment:
 *   'products/hr-management-software' → Home → Products → <page title>
 */

export interface Crumb {
  name: string;
  /** Absolute when a baseUrl is given (schema), root-relative otherwise (links). */
  href: string;
}

/** "help-center" → "Help Center" (for intermediate, title-less segments). */
export function titleCaseSegment(segment: string): string {
  return segment
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * Build the breadcrumb trail for a page. Returns null on the homepage (a
 * "Home → Home" crumb is noise) or when there is no slug.
 *
 * @param slug    the page slug, e.g. 'products/hr-management-software'
 * @param title   the current page's title (used for the last crumb)
 * @param baseUrl optional origin to prefix hrefs with (absolute URLs for JSON-LD);
 *                omit for root-relative hrefs used by on-page <Link>s.
 */
export function breadcrumbTrail(slug: string, title: string, baseUrl = ''): Crumb[] | null {
  if (!slug || slug === 'home') return null;

  const segments = slug.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const crumbs: Crumb[] = [{ name: 'Home', href: baseUrl || '/' }];
  for (let i = 0; i < segments.length; i++) {
    const isLast = i === segments.length - 1;
    crumbs.push({
      name: isLast ? title || titleCaseSegment(segments[i]) : titleCaseSegment(segments[i]),
      href: `${baseUrl}/${segments.slice(0, i + 1).join('/')}`,
    });
  }
  return crumbs;
}
